import DropDownButton from 'devextreme-react/drop-down-button';
import { Item } from 'devextreme-react/data-grid';
import type { DataGridRef } from 'devextreme-react/data-grid';
import { useMemo, type ReactNode, type RefObject } from 'react';
import { useShellLayout } from '../shell/useShellLayout';
import { exportOpsGridCsv, exportOpsGridExcel } from './opsGridExport';
import { resetOpsGridLayout } from './opsDataGridDefaults';

export const OPS_TOOLBAR_BUTTON = {
  stylingMode: 'outlined' as const
};

/** Apply to toolbar items that should hide on narrow viewports (shown in the grid menu instead). */
export const OPS_GRID_TOOLBAR_DESKTOP_ONLY = 'ops-grid-toolbar-desktop-only';

/** Visual `|` between toolbar action groups (desktop only). */
export function OpsGridToolbarSeparator() {
  return (
    <Item location="after" cssClass={`${OPS_GRID_TOOLBAR_DESKTOP_ONLY} ops-grid-toolbar-separator-slot`}>
      <span className="ops-grid-toolbar-separator" aria-hidden>
        |
      </span>
    </Item>
  );
}

export interface OpsGridToolbarMenuItem {
  id: string;
  text: string;
  icon?: string;
  disabled?: boolean;
  onClick: () => void;
}

export interface OpsGridToolbarExportConfig {
  fileName: string;
  worksheetName?: string;
  hint?: string;
  onExportExcel?: () => void;
  onExportCsv?: () => void;
}

export interface OpsGridToolbarResetLayoutConfig {
  storageKey: string;
}

export interface OpsGridToolbarItemsProps {
  gridRef: RefObject<DataGridRef | null>;
  onRefresh: () => void;
  loading?: boolean;
  refreshHint?: string;
  newItem?: { hint: string; onClick: () => void };
  viewItem?: { hint: string; active: boolean; onClick: () => void };
  editItem?: { hint: string; active: boolean; onClick: () => void };
  deleteItem?: { hint: string; active: boolean; onClick: () => void };
  uploadSlot?: ReactNode;
  /** Mobile menu entry that opens the upload/import flow (replaces uploadSlot on narrow). */
  uploadMenuItem?: OpsGridToolbarMenuItem;
  export?: OpsGridToolbarExportConfig;
  resetLayout?: OpsGridToolbarResetLayoutConfig | { onReset: () => void };
  showColumnChooser?: boolean;
  /** Extra actions merged into the mobile hamburger menu (and hidden desktop slots when marked). */
  extraMenuItems?: OpsGridToolbarMenuItem[];
}

interface ToolbarMenuRow {
  id: string;
  text: string;
  icon?: string;
  disabled?: boolean;
}

function buildToolbarMenuItems(props: OpsGridToolbarItemsProps): {
  rows: ToolbarMenuRow[];
  handlers: Map<string, () => void>;
} {
  const {
    gridRef,
    onRefresh,
    loading,
    refreshHint = 'Refresh',
    newItem,
    viewItem,
    editItem,
    deleteItem,
    uploadMenuItem,
    export: exportConfig,
    resetLayout,
    showColumnChooser = true,
    extraMenuItems = []
  } = props;

  const grid = () => gridRef.current?.instance();
  const handlers = new Map<string, () => void>();
  const rows: ToolbarMenuRow[] = [];

  const push = (id: string, text: string, onClick: () => void, icon?: string, disabled?: boolean) => {
    rows.push({ id, text, icon, disabled });
    handlers.set(id, onClick);
  };

  for (const item of extraMenuItems) {
    push(item.id, item.text, item.onClick, item.icon, item.disabled);
  }

  push('refresh', refreshHint, onRefresh, 'refresh', loading);

  if (viewItem) {
    push('view', viewItem.hint, viewItem.onClick, 'eyeopen', !viewItem.active);
  }

  if (editItem) {
    push('edit', editItem.hint, editItem.onClick, 'edit', !editItem.active);
  }

  if (deleteItem) {
    push('delete', deleteItem.hint, deleteItem.onClick, 'trash', !deleteItem.active);
  }

  if (newItem) {
    push('new', newItem.hint, newItem.onClick, 'plus');
  }

  if (uploadMenuItem) {
    push(uploadMenuItem.id, uploadMenuItem.text, uploadMenuItem.onClick, uploadMenuItem.icon ?? 'upload', uploadMenuItem.disabled);
  }

  if (exportConfig) {
    const worksheetName = exportConfig.worksheetName ?? exportConfig.fileName ?? 'Export';
    push(
      'export-excel',
      'Export Excel',
      () => {
        if (exportConfig.onExportExcel) exportConfig.onExportExcel();
        else void exportOpsGridExcel(grid(), exportConfig.fileName, worksheetName);
      },
      'exportxlsx'
    );
    push(
      'export-csv',
      'Export CSV',
      () => {
        if (exportConfig.onExportCsv) exportConfig.onExportCsv();
        else exportOpsGridCsv(grid(), exportConfig.fileName);
      },
      'export'
    );
  }

  if (resetLayout) {
    push('reset-layout', 'Reset column layout', () => {
      if ('onReset' in resetLayout) resetLayout.onReset();
      else resetOpsGridLayout(resetLayout.storageKey, gridRef);
    }, 'revert');
  }

  if (showColumnChooser) {
    push('column-chooser', 'Choose columns', () => grid()?.showColumnChooser(), 'columnchooser');
  }

  return { rows, handlers };
}

function OpsGridToolbarMenu({
  items,
  handlers
}: {
  items: ToolbarMenuRow[];
  handlers: Map<string, () => void>;
}) {
  return (
    <DropDownButton
      className="ops-grid-toolbar-menu-btn"
      icon="menu"
      stylingMode="outlined"
      hint="Grid actions"
      displayExpr="text"
      keyExpr="id"
      dropDownOptions={{ width: 240, showTitle: false }}
      items={items}
      onItemClick={(e) => {
        const id = e.itemData?.id;
        if (typeof id === 'string') handlers.get(id)?.();
      }}
    />
  );
}

export function OpsGridToolbarItems(props: OpsGridToolbarItemsProps) {
  const { isNarrow } = useShellLayout();
  const {
    gridRef,
    onRefresh,
    loading,
    refreshHint = 'Refresh',
    newItem,
    viewItem,
    editItem,
    deleteItem,
    uploadSlot,
    uploadMenuItem,
    export: exportConfig,
    resetLayout,
    showColumnChooser = true
  } = props;

  const menu = useMemo(() => buildToolbarMenuItems(props), [
    props.extraMenuItems,
    props.uploadMenuItem,
    gridRef,
    onRefresh,
    loading,
    refreshHint,
    newItem,
    viewItem,
    editItem,
    deleteItem,
    uploadMenuItem,
    exportConfig,
    resetLayout,
    showColumnChooser
  ]);

  const grid = () => gridRef.current?.instance();

  const handleReset = () => {
    if (!resetLayout) return;
    if ('onReset' in resetLayout) resetLayout.onReset();
    else resetOpsGridLayout(resetLayout.storageKey, gridRef);
  };

  const exportHint = exportConfig?.hint ?? 'Export';
  const worksheetName = exportConfig?.worksheetName ?? exportConfig?.fileName ?? 'Export';

  if (isNarrow) {
    return (
      <Item location="after" cssClass="ops-grid-toolbar-menu-slot">
        <OpsGridToolbarMenu items={menu.rows} handlers={menu.handlers} />
      </Item>
    );
  }

  return (
    <>
      {viewItem ? (
        <Item
          location="after"
          widget="dxButton"
          options={{
            ...OPS_TOOLBAR_BUTTON,
            icon: 'eyeopen',
            hint: viewItem.hint,
            elementAttr: { title: viewItem.hint },
            type: viewItem.active ? 'default' : 'normal',
            stylingMode: viewItem.active ? 'contained' : 'outlined',
            onClick: viewItem.onClick
          }}
        />
      ) : null}
      {editItem ? (
        <Item
          location="after"
          widget="dxButton"
          options={{
            ...OPS_TOOLBAR_BUTTON,
            icon: 'edit',
            hint: editItem.hint,
            elementAttr: { title: editItem.hint },
            type: editItem.active ? 'default' : 'normal',
            stylingMode: editItem.active ? 'contained' : 'outlined',
            onClick: editItem.onClick
          }}
        />
      ) : null}
      {deleteItem ? (
        <Item
          location="after"
          widget="dxButton"
          options={{
            ...OPS_TOOLBAR_BUTTON,
            icon: 'trash',
            hint: deleteItem.hint,
            elementAttr: { title: deleteItem.hint },
            type: deleteItem.active ? 'danger' : 'normal',
            stylingMode: deleteItem.active ? 'contained' : 'outlined',
            onClick: deleteItem.onClick
          }}
        />
      ) : null}
      {viewItem || editItem || deleteItem ? <OpsGridToolbarSeparator /> : null}
      {newItem ? (
        <Item
          location="after"
          widget="dxButton"
          options={{
            icon: 'plus',
            hint: newItem.hint,
            elementAttr: { title: newItem.hint },
            type: 'default',
            stylingMode: 'contained',
            onClick: newItem.onClick
          }}
        />
      ) : null}
      {newItem && (exportConfig || uploadSlot) ? <OpsGridToolbarSeparator /> : null}
      {uploadSlot}
      {exportConfig ? (
        <Item
          location="after"
          widget="dxDropDownButton"
          options={{
            ...OPS_TOOLBAR_BUTTON,
            icon: 'export',
            hint: exportHint,
            elementAttr: { title: exportHint },
            displayExpr: 'text',
            keyExpr: 'id',
            dropDownOptions: { width: 160 },
            items: [
              { id: 'excel', text: 'Excel', icon: 'exportxlsx' },
              { id: 'csv', text: 'CSV', icon: 'export' }
            ],
            onItemClick: (e: { itemData?: { id?: string } }) => {
              const instance = grid();
              if (e.itemData?.id === 'excel') {
                if (exportConfig.onExportExcel) exportConfig.onExportExcel();
                else void exportOpsGridExcel(instance, exportConfig.fileName, worksheetName);
              } else if (e.itemData?.id === 'csv') {
                if (exportConfig.onExportCsv) exportConfig.onExportCsv();
                else exportOpsGridCsv(instance, exportConfig.fileName);
              }
            }
          }}
        />
      ) : null}
      {(viewItem || editItem || deleteItem || newItem || exportConfig || uploadSlot) ? (
        <OpsGridToolbarSeparator />
      ) : null}
      <Item
        location="after"
        widget="dxButton"
        options={{
          ...OPS_TOOLBAR_BUTTON,
          icon: 'refresh',
          hint: refreshHint,
          elementAttr: { title: refreshHint },
          disabled: loading,
          onClick: onRefresh
        }}
      />
      {resetLayout ? (
        <Item
          location="after"
          widget="dxButton"
          options={{
            ...OPS_TOOLBAR_BUTTON,
            icon: 'revert',
            hint: 'Reset column layout',
            elementAttr: { title: 'Reset column layout' },
            onClick: handleReset
          }}
        />
      ) : null}
      {showColumnChooser ? (
        <Item
          location="after"
          widget="dxButton"
          options={{
            ...OPS_TOOLBAR_BUTTON,
            icon: 'columnchooser',
            hint: 'Choose columns',
            elementAttr: { title: 'Choose columns' },
            onClick: () => grid()?.showColumnChooser()
          }}
        />
      ) : null}
    </>
  );
}
